import React, { useState } from 'react';
import MarkdownViewer from '../common/MarkdownViewer';

const RELIABILITY_SOURCE_LABELS = {
  'A': 'Completament fiable',
  'B': 'Normalment fiable',
  'C': 'Bastant fiable',
  'D': 'No sempre fiable',
  'E': 'No fiable',
  'F': 'No es pot jutjar'
};

const RELIABILITY_INFO_LABELS = {
  '1': 'Confirmada',
  '2': 'Probablement certa',
  '3': 'Possiblement certa',
  '4': 'Dubtosa',
  '5': 'Improbable',
  '6': 'No es pot jutjar'
};

const EVIDENCE_TYPE_LABELS = {
  'document': 'Document',
  'intelligence': 'Intel',
  'osint': 'OSINT',
  'humint': 'HUMINT',
  'sigint': 'SIGINT',
  'media': 'Media',
  'technical': 'Tech'
};

function EvidencePanel({ evidence, collapsed, onToggle }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedEvidence, setSelectedEvidence] = useState(null);

  const filteredEvidence = evidence.filter(e => {
    const matchesSearch = !searchTerm ||
      e.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || e.evidenceType === filterType;
    return matchesSearch && matchesType;
  });

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
  };

  const getReliabilityColor = (source, info) => {
    if (!source || !info) return 'bg-surface-muted text-content-secondary';
    const sourceNum = source.charCodeAt(0) - 64; // A=1, B=2, etc.
    const infoNum = parseInt(info);
    const avg = (sourceNum + infoNum) / 2;
    if (avg <= 2) return 'bg-success-subtle text-success-text';
    if (avg <= 3.5) return 'bg-warning-subtle text-warning-text';
    return 'bg-error-subtle text-error-text';
  };

  if (collapsed) {
    return (
      <div className="w-12 bg-surface-subtle border-l border-border flex flex-col items-center py-4">
        <button
          onClick={onToggle}
          className="p-2 text-content-muted hover:text-content-secondary"
          title="Mostrar evidències"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </button>
        <span className="text-xs text-content-muted mt-2 writing-mode-vertical">{evidence.length}</span>
      </div>
    );
  }

  return (
    <div className="w-80 bg-surface-subtle border-l border-border flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-border bg-surface">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-content flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Evidències ({evidence.length})
          </h3>
          <button
            onClick={onToggle}
            className="p-1 text-content-disabled hover:text-content-secondary"
            title="Amagar panell"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Cercar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-1.5 text-sm border border-border-strong rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content placeholder-content-disabled"
        />

        {/* Filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="w-full mt-2 px-3 py-1.5 text-sm border border-border-strong rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content"
        >
          <option value="">Tots els tipus</option>
          {Object.entries(EVIDENCE_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Evidence List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {filteredEvidence.length === 0 ? (
          <p className="text-sm text-content-muted text-center py-4">
            No hi ha evidències
          </p>
        ) : (
          filteredEvidence.map((e) => (
            <div
              key={e.id}
              className="bg-surface rounded border border-border hover:border-border-strong transition-colors"
            >
              <div className="p-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm font-medium text-accent">
                        {e.code}
                      </span>
                      {e.reliabilitySource && e.reliabilityInfo && (
                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${getReliabilityColor(e.reliabilitySource, e.reliabilityInfo)}`}>
                          {e.reliabilitySource}{e.reliabilityInfo}
                        </span>
                      )}
                      {e.evidenceType && (
                        <span className="px-1.5 py-0.5 text-xs bg-surface-muted text-content-secondary rounded">
                          {EVIDENCE_TYPE_LABELS[e.evidenceType] || e.evidenceType}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-content mt-1 truncate">{e.title}</p>
                  </div>
                </div>

                <div className="flex items-center justify-end mt-2 space-x-1">
                  <button
                    onClick={() => copyCode(e.code)}
                    className="p-1 text-content-disabled hover:text-content-secondary"
                    title="Copiar codi"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setSelectedEvidence(e)}
                    className="p-1 text-content-disabled hover:text-content-secondary"
                    title="Veure detall"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Evidence Detail Modal */}
      {selectedEvidence && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-border flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-lg font-medium text-accent">
                    {selectedEvidence.code}
                  </span>
                  <span className="text-lg font-medium text-content">
                    - {selectedEvidence.title}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedEvidence(null)}
                className="p-1 text-content-disabled hover:text-content-secondary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[75vh]">
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <span className="text-content-muted">Tipus:</span>
                  <span className="ml-2 font-medium text-content">
                    {EVIDENCE_TYPE_LABELS[selectedEvidence.evidenceType] || selectedEvidence.evidenceType || '-'}
                  </span>
                </div>
                <div>
                  <span className="text-content-muted">Fiabilitat:</span>
                  <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded ${getReliabilityColor(selectedEvidence.reliabilitySource, selectedEvidence.reliabilityInfo)}`}>
                    {selectedEvidence.reliabilitySource || '-'}{selectedEvidence.reliabilityInfo || '-'}
                  </span>
                </div>
                {selectedEvidence.source && (
                  <div className="col-span-2">
                    <span className="text-content-muted">Font:</span>
                    <span className="ml-2 font-medium text-content">{selectedEvidence.source}</span>
                  </div>
                )}
                {selectedEvidence.classification && (
                  <div className="col-span-2">
                    <span className="text-content-muted">Classificació:</span>
                    <span className="ml-2 font-medium text-content capitalize">{selectedEvidence.classification}</span>
                  </div>
                )}
              </div>

              {selectedEvidence.reliabilitySource && (
                <div className="mb-4 p-2 bg-surface-subtle rounded text-xs text-content-secondary">
                  <p><strong>Font:</strong> {RELIABILITY_SOURCE_LABELS[selectedEvidence.reliabilitySource]}</p>
                  <p><strong>Info:</strong> {RELIABILITY_INFO_LABELS[selectedEvidence.reliabilityInfo]}</p>
                </div>
              )}

              <div className="border-t border-border pt-4">
                <h4 className="font-medium text-content mb-2">Contingut</h4>
                {selectedEvidence.content ? (
                  <MarkdownViewer content={selectedEvidence.content} />
                ) : (
                  <p className="text-content-muted italic">Sense contingut</p>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-end">
              <button
                onClick={() => {
                  copyCode(selectedEvidence.code);
                  setSelectedEvidence(null);
                }}
                className="px-4 py-2 text-sm bg-accent text-white rounded hover:bg-accent-hover"
              >
                Copiar codi {selectedEvidence.code}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EvidencePanel;
